import { motion } from 'framer-motion'
import { CheckCircleIcon, SparklesIcon } from '@heroicons/react/24/solid'

export default function PricingSection({ t }) {
  const scrollToCapture = () => {
    document.getElementById('email-capture')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <section className="section-padding">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-5xl font-display font-bold text-neutral-900">
            {t.pricing.title}
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 items-stretch">
          {/* Standard Pricing */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-white rounded-3xl p-8 shadow-lg border-2 border-neutral-200"
          >
            <div className="text-center mb-8">
              <p className="text-sm text-neutral-500 mb-2">
                {t.pricing.title === 'シンプルな料金体系' ? '通常価格' : 'Standard Price'}
              </p>
              <div className="text-5xl font-bold text-neutral-900 mb-2">
                {t.pricing.monthlyPrice}
              </div>
              <p className="text-sm text-neutral-600">
                {t.pricing.comparison}
              </p>
            </div>

            <ul className="space-y-4">
              {[
                t.pricing.title === 'シンプルな料金体系' ? '無制限チェックイン' : 'Unlimited check-ins',
                t.pricing.title === 'シンプルな料金体系' ? '写真付きチェックイン 📸' : 'Photo check-ins 📸',
                t.pricing.title === 'シンプルな料金体系' ? '最大5人の連絡先' : 'Up to 5 contacts',
                t.pricing.title === 'シンプルな料金体系' ? '自動アラート' : 'Automatic alerts',
                t.pricing.title === 'シンプルな料金体系' ? 'カスタマイズ可能なメッセージ' : 'Customizable messages',
                t.pricing.title === 'シンプルな料金体系' ? '日本語・英語サポート' : 'Japanese & English support'
              ].map((feature, index) => (
                <li key={index} className="flex items-center gap-3">
                  <CheckCircleIcon className="h-6 w-6 text-neutral-400 flex-shrink-0" />
                  <span className="text-neutral-700">{feature}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Early Bird Pricing - Featured */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-3xl p-8 shadow-2xl text-white relative overflow-hidden"
          >
            {/* Badge */}
            <div className="absolute top-0 right-0 bg-yellow-400 text-neutral-900 px-4 py-2 rounded-bl-2xl font-bold text-sm flex items-center gap-1">
              <SparklesIcon className="h-4 w-4" />
              {t.pricing.earlyBird.title}
            </div>

            <div className="text-center mb-8 mt-8">
              <p className="text-sm text-primary-100 mb-2">
                {t.pricing.earlyBird.discount}
              </p>
              <div className="text-5xl font-bold mb-2">
                {t.pricing.earlyBird.discountedPrice}
              </div>
              <p className="text-sm text-primary-100 line-through">
                {t.pricing.monthlyPrice}
              </p>
            </div>

            <ul className="space-y-4 mb-8">
              {[
                t.pricing.title === 'シンプルな料金体系' ? 'すべての標準機能' : 'All standard features',
                t.pricing.title === 'シンプルな料金体系' ? '写真付きチェックイン 📸' : 'Photo check-ins 📸',
                t.pricing.title === 'シンプルな料金体系' ? '創設メンバーバッジ' : 'Founding member badge',
                t.pricing.title === 'シンプルな料金体系' ? '永久に ¥500/月' : 'Locked at ¥500/month forever',
                t.pricing.title === 'シンプルな料金体系' ? '優先サポート' : 'Priority support',
                t.pricing.title === 'シンプルな料金体系' ? '将来の機能への早期アクセス' : 'Early access to future features'
              ].map((feature, index) => (
                <li key={index} className="flex items-center gap-3">
                  <CheckCircleIcon className="h-6 w-6 text-primary-200 flex-shrink-0" />
                  <span className="text-white font-medium">{feature}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={scrollToCapture}
              className="w-full bg-white text-primary-600 font-bold py-4 rounded-2xl hover:bg-primary-50 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95"
            >
              {t.pricing.ctaButton}
            </button>

            <p className="text-center text-sm text-primary-100 mt-4">
              {t.pricing.ctaNote}
            </p>
          </motion.div>
        </div>

        {/* Trust Message */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-12"
        >
          <p className="text-neutral-600">
            {t.pricing.title === 'シンプルな料金体系' 
              ? 'いつでもキャンセル可能。契約は不要です。' 
              : 'Cancel anytime. No contracts required.'}
          </p>
        </motion.div>
      </div>
    </section>
  )
}
